/** Two-letter language code of every language the suite ships. */
export type SupportedLanguage = 'de' | 'en' | 'fr' | 'nl' | 'pl' | 'tr';

/**
 * Language code -> locale id, matching the `i18n.locales` keys in the apps' project.json
 * and therefore the `messages.<locale>.xlf` file names.
 */
export const SUPPORTED_LOCALES: Record<SupportedLanguage, string> = {
    de: 'de-DE',
    en: 'en',
    fr: 'fr-FR',
    nl: 'nl-NL',
    pl: 'pl-PL',
    tr: 'tr-TR',
};

/** The apps are authored in German; there is no translation file for it. */
export const SOURCE_LANGUAGE: SupportedLanguage = 'de';
export const SOURCE_LOCALE = SUPPORTED_LOCALES[SOURCE_LANGUAGE];

export const SUPPORTED_LANGUAGES = Object.keys(SUPPORTED_LOCALES) as SupportedLanguage[];

/**
 * The browser's language as a bare two-letter code, or undefined outside a browser.
 *
 * Replaces transloco's `getBrowserLang()`. Note this is the *browser's* preference, which is not
 * necessarily the locale the app is running in — for that, inject `LOCALE_ID`.
 */
export function browserLanguage(): string | undefined {
    if (typeof navigator === 'undefined') return undefined;

    const tag = navigator.language;

    return tag ? tag.split(/[-_]/)[0] : undefined;
}

/** Maps an arbitrary language tag ("fr", "fr-CA", "PL-pl") onto a supported language. */
export function toSupportedLanguage(languageTag: string | null | undefined): SupportedLanguage | undefined {
    if (!languageTag) return undefined;

    const code = languageTag.substring(0, 2).toLowerCase();

    return SUPPORTED_LANGUAGES.find(language => language === code);
}

/** Maps an arbitrary language tag onto a locale id, or undefined when unsupported. */
export function toSupportedLocale(languageTag: string | null | undefined): string | undefined {
    const language = toSupportedLanguage(languageTag);

    return language && SUPPORTED_LOCALES[language];
}

/**
 * Reverses SUPPORTED_LOCALES: locale id -> language code.
 *
 * Falls back to the language subtag, so a region-qualified tag that is not itself a value in
 * SUPPORTED_LOCALES (`en-US`, where the map holds the bare `en`) resolves to its language instead
 * of silently landing on German.
 */
export function toLanguage(locale: string): SupportedLanguage {
    return SUPPORTED_LANGUAGES.find(language => SUPPORTED_LOCALES[language] === locale) ?? toSupportedLanguage(locale) ?? SOURCE_LANGUAGE;
}
